import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { producersColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'
import { ProducerService } from '../../services/entity-services/producer.service'

const DEFAULT_FILTERS = {
  name: '',
}

@Component({
  selector: 'app-producers',
  templateUrl: 'producers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProducersComponent extends BaseListing implements OnInit {
  private producerService = inject(ProducerService)

  listingColumns = signal(producersColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(structuredClone(DEFAULT_FILTERS))

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const f: Record<string, any> = {}
    const value = this.filters()

    if (value.name?.length) f['name'] = value.name
    return f
  })

  readonly queryProducers = injectInfiniteQuery(() => ({
    queryKey: ['producers', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      return await this.producerService
        .getProducers(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          { id: null, name: null }
        )
        .then(result => {
          this.saveViewSettings('producers', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.queryProducers.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryProducers.data()
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('producers')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('producers')
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryProducers.refetch()
  }

  setFilters(property: string, value: any, dateLeftRight?: string) {
    this.filters.set({
      ...this.filters(),
      [property]: value,
    })
  }
}
